import { max } from 'date-fns';
import Config from 'config';
import useStore from 'store';
import { LicenseStatus } from 'types/enums';
import {
  ProductType,
  Product,
  UserLicenseUsage,
  UserTrialUsage,
  App
} from 'types/schema-generated-types';
import { PliManagedApp } from 'store/cache/product-cache';
import { PrivilegesCache } from 'store/user';

export type Props = { statuses?: LicenseStatus[]; types?: ProductType[] };

type AppAccessState = {
  apps: AppLicenseStatus[];
};

export type AppLicenseStatus = {
  pliManagedApp: PliManagedApp;
  status?: LicenseStatus;
  dateExpiration?: Date;
};

export type UseAppAccess = [AppAccessState];

const { appAuditorPrivilegeMap, appTileOverrides } = Config;

const isFullOrAuditor = (status?: LicenseStatus) =>
  status === LicenseStatus.FULL || status === LicenseStatus.AUDITOR;

const isProduct = (products: Product[]) => (usage: UserLicenseUsage | UserTrialUsage): boolean =>
  products.some(p => p.id === usage.productId);

const getAppProducts = (pliManagedApp: PliManagedApp): Product[] => {
  if (pliManagedApp.appProduct?.product) return [pliManagedApp.appProduct.product];

  return pliManagedApp.app.products ?? [];
};

const getAppLicenseStatus = (
  app: App,
  privilegesCache: PrivilegesCache,
  licenseUsage: UserLicenseUsage[],
  trialUsage: UserTrialUsage[]
): Pick<AppLicenseStatus, 'status' | 'dateExpiration'> | null => {
  if (licenseUsage.some(u => u.active && !u.trial)) return { status: LicenseStatus.FULL };

  const appAuditorPrivileges = appAuditorPrivilegeMap.get(app.name) ?? [];
  for (const auditorPrivilege of appAuditorPrivileges) {
    if (privilegesCache.isPrivileged([auditorPrivilege])) return { status: LicenseStatus.AUDITOR };
  }

  if (licenseUsage.some(u => !u.active && !u.trial)) return { status: LicenseStatus.EXPIRED };

  const activeTrialUsage = trialUsage.filter(u => u.active);
  if (activeTrialUsage.length > 0) {
    const expirationDates = activeTrialUsage
      .filter(u => !!u.dateExpiration)
      .map(u => new Date(u.dateExpiration!));

    let dateExpiration: Date | undefined;
    if (expirationDates.length > 0) dateExpiration = max(expirationDates);

    return { status: LicenseStatus.TRIAL, dateExpiration };
  }

  if (trialUsage.some(u => u.available)) return { status: LicenseStatus.TRIAL_AVAILABLE };
  if (trialUsage.some(u => !u.active && !u.available)) return { status: LicenseStatus.EXPIRED };

  return null;
};

const useAppAccess = ({ statuses, types }: Props = {}): UseAppAccess => {
  const [
    {
      user: { me },
      productCache,
      userPrivilegesCache
    }
  ] = useStore();

  const pliManagedApps = productCache.getApps();
  const licenseUsage = me?.licenseUsage ?? [];
  const trialUsage = me?.trialUsage ?? [];

  const apps: AppLicenseStatus[] = [];
  for (const pliManagedApp of pliManagedApps) {
    const products = getAppProducts(pliManagedApp);

    const productUsage = licenseUsage.filter(isProduct(products));
    const productTrialUsage = trialUsage.filter(isProduct(products));
    const appType = pliManagedApp.app.type;

    if (types && (!appType || !types.includes(appType))) continue;
    if (appType === ProductType.STUDENT) apps.push({ pliManagedApp });
    if (appType === ProductType.USER) {
      const licenseStatus = getAppLicenseStatus(
        pliManagedApp.app,
        userPrivilegesCache,
        productUsage,
        productTrialUsage
      );

      if (!statuses || (licenseStatus?.status && statuses.includes(licenseStatus.status))) {
        apps.push({ pliManagedApp, ...licenseStatus });
      }
    }
  }

  // Filter out apps that have been overridden to be hidden in the config
  const filtered = apps.filter(({ pliManagedApp, status }, _, apps) => {
    const app = pliManagedApp.app;
    const override = appTileOverrides.find(o => o.app === app.name || o.appToSuppress === app.name);

    if (!override) return true;

    const otherAppName = app.name === override.app ? override.appToSuppress : override.app;
    const otherApp = apps.find(a => a.pliManagedApp.app.name === otherAppName);

    if (!otherApp) return true; // Only one of the apps is present, nothing to suppress.

    const isCurrentFullOrAuditor = isFullOrAuditor(status);
    const isOtherFullOrAuditor = isFullOrAuditor(otherApp.status);

    // Only suppress this app when it's not full/auditor and the other app is.
    if (app.name === override.app) return isCurrentFullOrAuditor || !isOtherFullOrAuditor;

    return isCurrentFullOrAuditor && !isOtherFullOrAuditor;
  });

  return [{ apps: filtered }];
};

export default useAppAccess;
